import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGES, STAGE_COLORS, fmtMoney } from "@/lib/constants";
import LeadFormDialog from "@/components/LeadFormDialog";
import LeadImportDialog from "@/components/LeadImportDialog";
import LeadImportHistoryDialog from "@/components/LeadImportHistoryDialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MagnifyingGlass, Buildings, EnvelopeSimple, Phone, Sparkle, PencilSimple, Paperclip } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export default function Leads() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const canEdit = hasRole("vanzari", "pm");

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const rescore = async (e, id) => {
    e.stopPropagation();
    setBusyId(id);
    try {
      const { data } = await api.post(`/leads/${id}/rescore`);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, ...data } : l)));
      toast.success("Scor AI recalculat");
    } catch { toast.error("Eroare la scorare"); }
    finally { setBusyId(null); }
  };

  const filtered = useMemo(() => leads.filter((l) => {
    if (stage !== "all" && l.stage !== stage) return false;
    if (!q) return true;
    const s = `${l.company} ${l.contact?.name} ${l.contact?.email} ${l.source} ${l.owner_name}`.toLowerCase();
    return s.includes(q.toLowerCase());
  }), [leads, q, stage]);

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 fade-up" data-testid="leads-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">CRM</p>
          <h1 className="font-head text-4xl font-extrabold tracking-tighter">Lead-uri</h1>
          <p className="mt-1 text-sm text-muted-foreground">Gestionează și califică lead-urile tale</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <LeadImportHistoryDialog onChanged={load} />
            <LeadImportDialog onImported={load} />
            <LeadFormDialog onCreated={(lead) => setLeads((ls) => [lead, ...ls])} triggerLabel="Lead Nou" />
          </div>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Caută lead-uri…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="leads-search" />
        </div>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-56" data-testid="leads-stage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate statusurile</SelectItem>
            {STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Nume</th>
              <th className="px-4 py-3 text-left font-semibold">Contact</th>
              <th className="px-4 py-3 text-left font-semibold">Companie</th>
              <th className="px-4 py-3 text-left font-semibold">Sursă</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-left font-semibold">Scor AI</th>
              <th className="px-4 py-3 text-left font-semibold">Agent</th>
              <th className="px-4 py-3 text-right font-semibold">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((l) => {
              const sc = STAGE_COLORS[l.stage] || STAGE_COLORS.nou;
              const low = (l.ai_score ?? 0) < 40;
              return (
                <tr key={l.id} onClick={() => navigate(`/leads/${l.id}`)}
                  className="group cursor-pointer transition-colors hover:bg-white/[0.03]" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 font-semibold">{l.contact?.name || l.company}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                      {l.contact?.email && <span className="flex items-center gap-1.5"><EnvelopeSimple size={13} /> {l.contact.email}</span>}
                      {l.contact?.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {l.contact.phone}</span>}
                      {!l.contact?.email && !l.contact?.phone && "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5"><Buildings size={14} className="text-muted-foreground" /> {l.company}
                      {l.doc_count > 0 && <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-blue-300" title={`${l.doc_count} document(e) pe lead`} data-testid={`lead-doccount-${l.id}`}><Paperclip size={10} /> {l.doc_count}</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{l.source || "—"}</td>
                  <td className="px-4 py-3"><span className={cn("rounded-md px-2 py-0.5 text-[11px] font-semibold", sc.pill)}>{sc.statusLabel}</span></td>
                  <td className="px-4 py-3"><span className={cn("font-bold tabular-nums", low ? "text-red-400" : "text-emerald-400")}>{l.ai_score ?? "—"}{l.ai_score != null ? "/100" : ""}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{l.owner_name || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      {canEdit && (
                        <button onClick={(e) => rescore(e, l.id)} disabled={busyId === l.id}
                          className="rounded-md bg-violet-500/10 p-1.5 text-violet-400 transition-colors hover:bg-violet-500/20 disabled:opacity-50"
                          title="Rescor AI" data-testid={`lead-ai-${l.id}`}>
                          <Sparkle size={15} weight="fill" />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/leads/${l.id}`); }}
                        className="rounded-md border border-border p-1.5 text-muted-foreground transition-colors hover:text-foreground"
                        title="Deschide" data-testid={`lead-edit-${l.id}`}>
                        <PencilSimple size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">Niciun lead găsit.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
